/**
 * Formatos, etiquetas y validaciones compartidas por las pantallas de stock de Técnica.
 *
 * Todo lo de acá es puro (sin React ni API) para poder probarlo solo: las validaciones de
 * cantidades replican las del backend (TecnicaStockValidacion) para deshabilitar Guardar
 * antes de mandar algo que igual va a volver con 400.
 */

const SIN_DATO = "—";

// ── Niveles de permiso ───────────────────────────────────────

/** Orden de los niveles: cada uno incluye lo que puede el anterior. */
export const NIVELES_STOCK = { CONSULTA: 1, PANOL: 2, GESTION: 3 };

/**
 * ¿El nivel `actual` alcanza para `requerido`? Un `requerido` desconocido es un error de
 * programación (un typo en la pantalla), así que lanza en vez de negar en silencio.
 */
export function alcanzaNivel(actual, requerido) {
    const pedido = NIVELES_STOCK[requerido];
    if (!pedido) throw new Error(`Nivel de stock desconocido: ${requerido}`);
    const tiene = NIVELES_STOCK[actual];
    return !!tiene && tiene >= pedido;
}

// ── Etiquetas ────────────────────────────────────────────────

export const TIPO_ARTICULO = {
    CONSUMIBLE: "Consumible",
    HERRAMIENTA: "Herramienta",
    EQUIPO: "Equipo",
};

export const SEGUIMIENTO = {
    CANTIDAD: "Por cantidad",
    UNIDAD: "Por unidad",
};

export const UNIDAD_MEDIDA = {
    UNIDAD: "Unidad",
    METRO: "Metro",
    KILO: "Kilo",
    LITRO: "Litro",
    PAR: "Par",
};

const UNIDAD_CORTA = {
    UNIDAD: "u.",
    METRO: "m",
    KILO: "kg",
    LITRO: "l",
    PAR: "pares",
};

export const TIPO_MOVIMIENTO = {
    INGRESO: "Ingreso",
    ENTREGA: "Entrega",
    DEVOLUCION: "Devolución",
    TRASPASO: "Traspaso",
    CONSUMO_OT: "Consumo en OT",
    INSTALACION: "Instalación",
    RETIRO: "Retiro",
    REVERSION: "Reversión",
    AJUSTE_POSITIVO: "Ajuste +",
    AJUSTE_NEGATIVO: "Ajuste −",
    BAJA: "Baja",
    CAMBIO_ESTADO: "Cambio de estado",
};

export const TIPO_REMITO = {
    INGRESO: "Ingreso",
    ENTREGA: "Entrega",
    DEVOLUCION: "Devolución",
    TRASPASO: "Traspaso",
    ANULACION: "Anulación",
};

export const TIPO_UBICACION = {
    DEPOSITO: "Depósito",
    TECNICO: "Técnico",
};

/** Sufijo de `ds-badge--*` por tipo de movimiento: lo que entra, lo que sale y lo que circula. */
export const BADGE_MOVIMIENTO = {
    INGRESO: "success",
    AJUSTE_POSITIVO: "success",
    AJUSTE_NEGATIVO: "danger",
    BAJA: "danger",
    ENTREGA: "info",
    TRASPASO: "info",
    CAMBIO_ESTADO: "warning",
    REVERSION: "neutral",
};

export const BADGE_REMITO = {
    INGRESO: "success",
    ENTREGA: "info",
    DEVOLUCION: "neutral",
    TRASPASO: "info",
    ANULACION: "danger",
};

/**
 * Estado de la recepción de un remito (lo calcula el backend). NO_APLICA es un ingreso, una
 * devolución o una anulación: nadie tiene que confirmarlos, así que no se muestra nada.
 */
export const ESTADO_RECEPCION = {
    NO_APLICA: null,
    PENDIENTE: "Sin confirmar",
    CONFIRMADO: "Confirmado",
    RECHAZADO: "Rechazado",
    ANULADO: "Anulado",
};

export const BADGE_ESTADO_RECEPCION = {
    PENDIENTE: "warning",
    CONFIRMADO: "success",
    RECHAZADO: "danger",
    ANULADO: "neutral",
};

export const CAUSA_ANULACION = {
    ERROR: "Error de carga",
    RECHAZO_RECEPTOR: "Rechazo del receptor",
};

export const RECEPCION_MODO = {
    PANOL: "firmado en el pañol",
    APP: "desde la app",
    WEB: "desde la web",
};

export const NIVEL_STOCK = {
    CONSULTA: "Consulta",
    PANOL: "Pañol",
    GESTION: "Gestión",
};

export const BADGE_NIVEL = {
    CONSULTA: "neutral",
    PANOL: "info",
    GESTION: "primary",
};

/** Etiqueta de un mapa, o el valor crudo si no está (un valor nuevo del backend se ve igual). */
export const etiqueta = (mapa, valor) => (valor ? mapa[valor] || valor : SIN_DATO);

export const badgeDe = (mapa, valor) => `ds-badge ds-badge--${mapa[valor] || "neutral"}`;

// ── Números ──────────────────────────────────────────────────

const fmtCantidad = new Intl.NumberFormat("es-AR", { maximumFractionDigits: 2 });
const fmtPesos = new Intl.NumberFormat("es-AR", { style: "currency", currency: "ARS" });

/** La etiqueta corta de una unidad del catálogo (METRO → «m», UNIDAD → «u.»); una desconocida, tal cual. */
export const unidadCorta = (unidad) => UNIDAD_CORTA[unidad] || unidad || "";

/** 1.5 + METRO → "1,5 m". Sin unidad, sólo el número. */
export function formatCantidad(v, unidad) {
    if (v === null || v === undefined || v === "") return SIN_DATO;
    const n = Number(v);
    if (!Number.isFinite(n)) return SIN_DATO;
    const corta = UNIDAD_CORTA[unidad];
    return corta ? `${fmtCantidad.format(n)} ${corta}` : fmtCantidad.format(n);
}

/** Pesos con centavos; null es «sin costo cargado», no cero. */
export function formatPesos(v) {
    if (v === null || v === undefined || v === "") return SIN_DATO;
    const n = Number(v);
    return Number.isFinite(n) ? fmtPesos.format(n) : SIN_DATO;
}

/** 123 → "R-000123". El backend ya manda `numero_texto`; esto es para cuando sólo hay el número. */
export function numeroRemito(n) {
    if (n === null || n === undefined || n === "") return SIN_DATO;
    return "R-" + String(n).padStart(6, "0");
}

/** En UNIDAD y PAR no hay fracciones: el backend rechaza decimales. */
export function esEntera(unidad) {
    return unidad === "UNIDAD" || unidad === "PAR";
}

export const MAX_CANTIDAD_ENTEROS = 10; // NUMERIC(12,2): hasta 9.999.999.999,99
export const MAX_COSTO_ENTEROS = 12; // NUMERIC(14,2): hasta 999.999.999.999,99

/**
 * Tope del texto de los buscadores: es el MAX_Q de TecnicaStockParametros. Pasado eso el backend
 * contesta 400 y el listado queda en error hasta borrar la búsqueda, así que mejor no dejar escribirlo.
 */
export const MAX_BUSQUEDA = 100;

/**
 * Lee un número escrito en un input y lo valida como lo hace el backend: sin redondear
 * (más de 2 decimales es error, no se recorta), entero si la unidad lo pide y con el tope
 * de dígitos de la columna. Devuelve `{ valor, error }`; vacío da `valor: null`.
 *
 * @param {string|number} texto
 * @param {{ unidad?: string, maxEnteros?: number, obligatorio?: boolean, minimo?: "positivo"|"cero" }} [opciones]
 */
export function leerNumero(texto, opciones = {}) {
    const { unidad, maxEnteros = MAX_CANTIDAD_ENTEROS, obligatorio = true, minimo = "positivo" } = opciones;
    const t = texto === null || texto === undefined ? "" : String(texto).trim().replace(",", ".");
    if (!t) return { valor: null, error: obligatorio ? "Es obligatorio." : null };

    const m = /^(\d+)(?:\.(\d*))?$/.exec(t);
    if (!m) return { valor: null, error: "Ingresá un número válido." };

    const enteros = m[1].replace(/^0+(?=\d)/, "");
    // Los ceros al final no cuentan como decimales: 5,50 es lo mismo que 5,5.
    const decimales = (m[2] || "").replace(/0+$/, "");
    if (decimales.length > 2) return { valor: null, error: "Admite hasta 2 decimales." };
    if (decimales.length > 0 && esEntera(unidad)) {
        return { valor: null, error: unidad === "PAR" ? "Los pares se cargan enteros." : "Las unidades se cargan enteras." };
    }
    if (enteros.length > maxEnteros) return { valor: null, error: "El número es demasiado grande." };

    const valor = Number(t);
    if (minimo === "positivo" && !(valor > 0)) return { valor: null, error: "Tiene que ser mayor a 0." };
    return { valor, error: null };
}

// ── Otros ────────────────────────────────────────────────────

/**
 * Clave de idempotencia de una operación. Se genera al abrir el modal y se reusa en los
 * reintentos: si la primera respuesta se perdió, el backend devuelve lo que ya hizo en vez
 * de registrar el ingreso dos veces.
 */
export function nuevaClave() {
    const c = typeof window !== "undefined" ? window.crypto : undefined;
    if (c && typeof c.randomUUID === "function") return c.randomUUID();
    if (!c || typeof c.getRandomValues !== "function") {
        // Sin crypto no hay forma de sacar una clave que no choque: mejor avisar que inventarla.
        throw new Error("El navegador no permite generar la clave de la operación.");
    }
    const b = c.getRandomValues(new Uint8Array(16));
    b[6] = (b[6] & 0x0f) | 0x40; // versión 4
    b[8] = (b[8] & 0x3f) | 0x80; // variante RFC 4122
    const h = Array.from(b, (x) => x.toString(16).padStart(2, "0")).join("");
    return `${h.slice(0, 8)}-${h.slice(8, 12)}-${h.slice(12, 16)}-${h.slice(16, 20)}-${h.slice(20)}`;
}

/** El `code` de ResponseCode que manda el backend en el cuerpo del error (6641-6699 para stock). */
export const codigoError = (err) => err?.response?.data?.code ?? null;

export const COD_ARTICULO_MODIFICADO = 6648;
export const COD_ARTICULO_NO_EXISTE = 6642;
export const COD_SIN_SALDO = 6651;
export const COD_LEGAJO_DUPLICADO = 6655;
export const COD_SIN_LEGAJO = 6656;
export const COD_NO_ES_TECNICO = 6663;
export const COD_ROL_NO_VERIFICADO = 6671;

// ── Remitos a técnicos ───────────────────────────────────────

/** Es el HORAS_PENDIENTE_VENCIDO del backend: una entrega sin confirmar pasadas 48 h es un «pendiente vencido». */
export const HORAS_PENDIENTE_VENCIDO = 48;

/**
 * ¿Se puede anular? Una anulación no se anula, y un remito ya anulado o rechazado ya tiene la
 * suya. El backend lo vuelve a controlar (y además exige que el receptor todavía tenga todo).
 */
export function esAnulable(remito) {
    if (!remito) return false;
    return remito.tipo !== "ANULACION" && !remito.anulado_por_id && remito.estado_recepcion !== "RECHAZADO";
}

/** ¿Es una entrega o un traspaso hacia mi ubicación que todavía tengo que confirmar o rechazar? */
export function esPendienteParaMi(remito, ubicacionPropia) {
    if (!remito || !ubicacionPropia) return false;
    return remito.estado_recepcion === "PENDIENTE"
        && remito.destino_ubicacion_id === ubicacionPropia
        && (remito.tipo === "ENTREGA" || remito.tipo === "TRASPASO");
}

/**
 * ¿Pasaron más de 48 h desde `creado`? El backend manda la hora de Buenos Aires sin zona
 * ("2026-09-28T11:00:00"), que el navegador lee como hora local: en la Argentina es la misma.
 * Una fecha que no se puede leer da false: no se marca como vencido lo que no se sabe.
 */
export function esPendienteVencido(creado, ahora = new Date()) {
    if (!creado) return false;
    const t = new Date(creado).getTime();
    if (!Number.isFinite(t)) return false;
    return ahora.getTime() - t > HORAS_PENDIENTE_VENCIDO * 3600 * 1000;
}

// ── Firma en el pañol (tanda 5) ──────────────────────────────

/** Subtítulo del modal de firma: qué constancia deja la firma según el tipo de remito. */
export const TEXTO_FIRMA_PANOL = {
    ENTREGA: "Recepción de una entrega del pañol",
    TRASPASO: "Recepción de un traspaso",
    DEVOLUCION: "Devolución al pañol",
};

/**
 * ¿Se puede firmar en el pañol? Una entrega, una devolución o un traspaso que nadie confirmó,
 * rechazó ni anuló. Un ingreso o una anulación no llevan firma. El backend lo vuelve a controlar
 * y además rechaza al pañolero que quiere registrar su propia firma.
 */
export function esFirmableEnPanol(remito) {
    return ["ENTREGA", "DEVOLUCION", "TRASPASO"].includes(remito?.tipo)
        && !remito.recepcion_confirmada_at
        && !remito.rechazado_at
        && !remito.anulado_por_id;
}

// ── Herramientas y equipos por pieza (tanda 6) ───────────────

/**
 * El id fijo del depósito central: lo siembra el DDL 14 y no cambia nunca. Las pantallas lo usan
 * para pedir «las piezas disponibles del depósito» sin tener que buscar la ubicación antes.
 */
export const DEPOSITO_CENTRAL_ID = "00000000-0000-4000-8000-000000000001";

/** MAX_PIEZAS del backend: piezas por remito, aparte de los 100 renglones por cantidad. */
export const MAX_PIEZAS = 100;

/** Serie, marca y modelo son VARCHAR(80). */
export const MAX_TEXTO_PIEZA = 80;

export const ESTADO_PIEZA = {
    DISPONIBLE: "Disponible",
    EN_PODER: "En poder de un técnico",
    EN_REPARACION: "En reparación",
    VENDIDA: "Vendida",
    EN_COMODATO: "En comodato",
    BAJA: "Dada de baja",
};

export const BADGE_ESTADO_PIEZA = {
    DISPONIBLE: "success",
    EN_PODER: "info",
    EN_REPARACION: "warning",
    VENDIDA: "neutral",
    EN_COMODATO: "neutral",
    BAJA: "danger",
};

/** Cómo volvió una pieza en una devolución. */
export const ESTADO_DEVUELTO = { OK: "Bien", DANADA: "Dañada" };

export const BADGE_ESTADO_DEVUELTO = { OK: "success", DANADA: "danger" };

export const COD_PIEZA_NO_DISPONIBLE = 6675;
export const COD_SERIE_DUPLICADA = 6676;
export const COD_PIEZA_MODIFICADA = 6677;
export const COD_KIT_MODIFICADO = 6680;
export const COD_KIT_INCOMPLETO = 6682;
/** El 400 genérico del stock (DATO_INVALIDO): en la entrega del kit, «como mucho N» si el técnico recibió algo en el medio. */
export const COD_DATO_INVALIDO = 6641;

// ── Incidentes (tanda 7) ─────────────────────────────────────

export const COD_INCIDENTE_ESTADO = 6686;
export const COD_INCIDENTE_YA_DECLARADO = 6687;
export const COD_DESCARGO_YA_ESCRITO = 6688;
export const COD_REVISION_DESACTUALIZADA = 6691;

// ── Insumos de la OT (tanda 8) ───────────────────────────────
// Están en el bloque de la OT (6636-6638) y no en el del stock: eran huecos libres de ese bloque.

/** Pasaron las 24 h desde que se completó la visita: el técnico ya no carga, lo hace Gestión. */
export const COD_INSUMO_FUERA_DE_PLAZO = 6636;
/** El insumo no está en el estado que la operación necesita (ya regularizado, anulado, no es una diferencia). */
export const COD_INSUMO_ESTADO = 6637;
/** La OT tiene movimientos, remitos, incidentes o diferencias: no se elimina, se cancela. */
export const COD_ORDEN_CON_STOCK = 6638;
/** El artículo está dado de baja. */
export const COD_ARTICULO_INACTIVO = 6646;

/**
 * La serie como la compara el backend (`upper(btrim(…))` de uq_tecnica_pieza_serie): «abc 1 » y
 * «ABC 1» son la misma. Vacío es "".
 */
export function normalizarSerie(texto) {
    return String(texto ?? "").trim().toUpperCase();
}

/**
 * «H-000007 · S/N ABC123 · Bosch GSB 13 RE», sin las partes que falten. Sirve para una PiezaDTO,
 * una PiezaSugeridaDTO o un renglón de remito (que trae los mismos datos con el prefijo `pieza_`).
 */
export function textoPieza(p) {
    if (!p) return "";
    const codigo = p.codigo ?? p.pieza_codigo;
    const serie = p.nro_serie ?? p.pieza_nro_serie;
    const marcaModelo = [p.marca ?? p.pieza_marca, p.modelo ?? p.pieza_modelo].filter(Boolean).join(" ");
    return [codigo, serie ? `S/N ${serie}` : null, marcaModelo || null].filter(Boolean).join(" · ");
}

/**
 * El número de «H-000007» (7). H- y E- salen de la misma secuencia, así que ordenar por el número
 * deja cada pieza en el orden en que entró, sea herramienta o equipo. Un código que no tiene esa
 * forma da null (no se inventa un orden).
 */
export function numeroDeCodigo(codigo) {
    const m = /^[A-Z]+-(\d+)$/.exec(String(codigo ?? "").trim());
    return m ? Number(m[1]) : null;
}

/**
 * Los códigos de un ingreso para rotular: «H-000007», «H-000007 y H-000008» o, con más,
 * «H-000007 a H-000012» (el menor y el mayor) sólo si son consecutivos. Salen de una secuencia,
 * pero dos ingresos simultáneos se intercalan los números (uno recibe 7, 9 y 11 y el otro 8, 10 y
 * 12): un rango «H-000007 a H-000011» haría rotular con códigos del otro ingreso. Si no son
 * consecutivos, van todos.
 */
export function resumenCodigos(codigos) {
    const lista = (codigos || []).filter(Boolean);
    if (!lista.length) return "";
    const orden = [...lista].sort((a, b) => (numeroDeCodigo(a) ?? 0) - (numeroDeCodigo(b) ?? 0));
    if (orden.length === 1) return orden[0];
    if (orden.length === 2) return `${orden[0]} y ${orden[1]}`;
    const numeros = orden.map(numeroDeCodigo);
    const consecutivos = numeros.every((n) => n !== null) && numeros[numeros.length - 1] - numeros[0] + 1 === numeros.length;
    return consecutivos ? `${orden[0]} a ${orden[orden.length - 1]}` : listaYCon(orden);
}

/** «A», «A y B», «A, B y C». */
export function listaYCon(items) {
    const lista = (items || []).filter((x) => x !== null && x !== undefined && x !== "");
    if (lista.length <= 1) return lista.join("");
    return `${lista.slice(0, -1).join(", ")} y ${lista[lista.length - 1]}`;
}

const aCentavos = (v) => Math.round(Number(v || 0) * 100);

/**
 * Lo que queda faltando en un kit después de una entrega: la misma cuenta que hace el backend
 * antes del 409 KIT_INCOMPLETO (requerido − tiene − entregado, en centavos para que 0,1 + 0,2 dé
 * exacto). Un artículo dado de baja no se exige: la plantilla lo muestra para que lo saquen.
 *
 * @param {Array} renglones  Los de KitArmadoDTO (articulo_id, articulo_nombre, unidad, articulo_activo, requerido, tiene).
 * @param {Object<string, number>} entregas  articulo_id → lo que se entrega ahora (cantidad o piezas).
 * @returns {Array<{articulo_id: string, articulo_nombre: string, unidad: string, falta: number}>}
 */
export function faltantesKit(renglones, entregas = {}) {
    return (renglones || [])
        .filter((r) => r.articulo_activo !== false)
        .map((r) => ({ r, falta: aCentavos(r.requerido) - aCentavos(r.tiene) - aCentavos(entregas[r.articulo_id]) }))
        .filter((x) => x.falta > 0)
        .map(({ r, falta }) => ({ articulo_id: r.articulo_id, articulo_nombre: r.articulo_nombre, unidad: r.unidad, falta: falta / 100 }));
}
